/**
 * ForecastControlPanel Component
 *
 * Control panel for the Forecast page with location, year, and month selectors,
 * plus a refresh button.
 */

import React from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { LocationDropdown } from '../LocationDropdown';
import { YearSelector } from '../YearSelector';
import { MonthSelector } from '../MonthSelector';

interface ForecastControlPanelProps {
  locationId: number;
  year: number;
  month: number;
  onLocationChange: (locationId: number) => void;
  onYearChange: (year: number) => void;
  onMonthChange: (month: number) => void;
  onRefresh: (loadHistorical?: boolean) => void;
  loading: boolean;
  refreshing: boolean;
  lastRefreshed?: string;
  hasHistoricalData?: boolean;
  canRefresh?: boolean; // Admin only - show refresh buttons
}

export const ForecastControlPanel: React.FC<ForecastControlPanelProps> = ({
  locationId,
  year,
  month,
  onLocationChange,
  onYearChange,
  onMonthChange,
  onRefresh,
  loading,
  refreshing,
  lastRefreshed,
  hasHistoricalData = false,
  canRefresh = true, // Default to true for backwards compatibility
}) => {
  const isDisabled = loading || refreshing;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
      <div className="flex flex-wrap items-center gap-4">
        {/* Location Selector */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Location:</label>
          <LocationDropdown
            value={locationId}
            onChange={onLocationChange}
            disabled={isDisabled}
          />
        </div>

        {/* Year Selector */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Year:</label>
          <YearSelector
            value={year}
            onChange={onYearChange}
            disabled={isDisabled}
          />
        </div>

        {/* Month Selector */}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-gray-700">Month:</label>
          <MonthSelector
            value={month}
            onChange={onMonthChange}
            disabled={isDisabled}
          />
        </div>

        {/* Refresh Buttons - Admin only */}
        {canRefresh && (
          <div className="ml-auto flex items-center gap-2">
            {/* Refresh Current Month */}
            <button
              onClick={() => onRefresh(false)}
              disabled={isDisabled}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              title="Refresh current month data from DealerBuilt"
            >
              <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </button>

            {/* Load Historical Data Button */}
            {!hasHistoricalData && (
              <button
                onClick={() => onRefresh(true)}
                disabled={isDisabled}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                title="Load current month + 3 years of historical data"
              >
                <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                Load Historical
              </button>
            )}
          </div>
        )}
      </div>

      {/* Last Refreshed Timestamp */}
      {lastRefreshed && (
        <div className="mt-2 text-xs text-gray-500">
          Last refreshed: {new Date(lastRefreshed).toLocaleString()}
        </div>
      )}
    </div>
  );
};
